import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Building2, Instagram, Twitter, Youtube, Facebook, Truck, ShieldCheck, RotateCw, Headphones } from 'lucide-react';
import { CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const addressLine2 = [
    CONTACT_INFO.address.city,
    CONTACT_INFO.address.state,
    CONTACT_INFO.address.zip,
  ].filter(Boolean).join(' ').trim();

  return (
    <footer className="relative bg-ink-950 text-white overflow-hidden">
      {/* Trust Strip */}
      <div className="bg-athletic-500 text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Truck, title: 'Fast USA Shipping', sub: 'Free on orders $75+' },
              { icon: ShieldCheck, title: 'Secure Checkout', sub: 'SSL encrypted payments' },
              { icon: RotateCw, title: '30-Day Returns', sub: 'Hassle-free policy' },
              { icon: Headphones, title: 'Real Support', sub: 'Athletes helping athletes' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-11 h-11 flex-shrink-0 bg-white/15 flex items-center justify-center">
                  <item.icon className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <p className="font-black text-sm uppercase tracking-wider leading-tight">{item.title}</p>
                  <p className="text-white/80 text-xs leading-tight mt-0.5">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Newsletter Block */}
      <div className="border-b border-white/10 bg-gradient-to-br from-ink-900 via-ink-950 to-ink-900">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-athletic-400 text-[11px] font-bold tracking-[0.35em] uppercase mb-3">Join The Movement</p>
              <h3 className="font-display text-4xl md:text-5xl leading-none tracking-wider">
                TRAIN HARDER.<br />
                <span className="text-athletic-500">GET EARLY DROPS.</span>
              </h3>
              <p className="text-white/60 mt-4 max-w-md">
                Sign up for exclusive product drops, training tips and member-only discounts.
              </p>
            </div>
            <form className="flex flex-col sm:flex-row gap-3 w-full" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="your@email.com"
                className="flex-1 px-5 py-4 bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-athletic-500 transition-colors"
              />
              <button type="submit" className="px-8 py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-black uppercase tracking-wider transition-colors">
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Grid */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10">

          {/* Brand - 4 cols */}
          <div className="col-span-2 lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-3 mb-5">
              <div className="relative w-12 h-12 bg-athletic-500 flex items-center justify-center">
                <span className="absolute -right-2 top-0 w-0 h-0 border-t-[48px] border-t-ink-950 border-l-[18px] border-l-transparent" />
                <span className="relative text-white font-display text-2xl leading-none pt-1">S</span>
              </div>
              <span className="font-display text-2xl leading-none tracking-wider">
                SPORT<span className="text-athletic-500">FITT</span>PRO
              </span>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed max-w-sm">
              Premium sports gear and fitness equipment built for athletes who refuse to settle. Gear that keeps up with your grind.
            </p>

            {/* Corporate info prominent */}
            <div className="mt-6 p-5 bg-white/5 border-l-4 border-athletic-500">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-athletic-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/50 mb-1">Company</p>
                  <p className="font-bold text-white">{CONTACT_INFO.corpName}</p>
                  <p className="text-white/50 text-xs mt-1">
                    Address: {CONTACT_INFO.address.street}
                    {addressLine2 ? <><br />{addressLine2}</> : null}
                    {CONTACT_INFO.address.country ? <><br />{CONTACT_INFO.address.country}</> : null}
                  </p>
                </div>
              </div>
            </div>

            {/* Social */}
            <div className="flex gap-2 mt-6">
              {[Instagram, Twitter, Youtube, Facebook].map((Icon, i) => (
                <a key={i} href="#" className="w-10 h-10 bg-white/5 hover:bg-athletic-500 border border-white/10 hover:border-athletic-500 flex items-center justify-center text-white/60 hover:text-white transition-all">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Shop - 2 cols */}
          <div className="lg:col-span-2">
            <h4 className="font-display text-xl tracking-wider mb-5 text-white">SHOP</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/shop" className="text-white/60 hover:text-athletic-400 text-sm transition-colors">All Products</Link>
              </li>
              {categories.slice(0, 6).map((cat) => (
                <li key={cat.id}>
                  <Link to={`/shop?category=${cat.slug}`} className="text-white/60 hover:text-athletic-400 text-sm transition-colors">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company - 2 cols */}
          <div className="lg:col-span-2">
            <h4 className="font-display text-xl tracking-wider mb-5 text-white">COMPANY</h4>
            <ul className="space-y-3">
              {[
                { name: 'About Us', path: '/about' },
                { name: 'Our Mission', path: '/our-mission' },
                { name: 'Why Choose Us', path: '/why-choose-us' },
                { name: 'Quality Guarantee', path: '/quality-guarantee' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/60 hover:text-athletic-400 text-sm transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support - 2 cols */}
          <div className="lg:col-span-2">
            <h4 className="font-display text-xl tracking-wider mb-5 text-white">SUPPORT</h4>
            <ul className="space-y-3">
              {[
                { name: 'FAQ', path: '/faq' },
                { name: 'Shipping', path: '/shipping' },
                { name: 'Returns & Refunds', path: '/returns' },
                { name: 'Privacy Policy', path: '/privacy' },
                { name: 'Terms & Conditions', path: '/terms' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/60 hover:text-athletic-400 text-sm transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact - 2 cols */}
          <div className="col-span-2 lg:col-span-2">
            <h4 className="font-display text-xl tracking-wider mb-5 text-white">CONTACT</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-start gap-3 text-white/60 hover:text-athletic-400 transition-colors">
                  <Phone className="w-4 h-4 mt-0.5 text-athletic-500" />
                  <span className="text-sm font-medium">{CONTACT_INFO.phone}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-3 text-white/60 hover:text-athletic-400 transition-colors">
                  <Mail className="w-4 h-4 mt-0.5 text-athletic-500" />
                  <span className="text-sm break-all">{CONTACT_INFO.email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/60">
                  <MapPin className="w-4 h-4 mt-0.5 text-athletic-500" />
                  <span className="text-sm">
                    {CONTACT_INFO.address.street}
                    {addressLine2 ? <><br />{addressLine2}</> : null}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Strip */}
      <div className="border-t border-white/10 bg-ink-950">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-x-4 gap-y-1 text-xs text-white/40">
              <span>© {currentYear} Sport Fitt Pro</span>
              <span className="hidden sm:block text-white/20">•</span>
              <span>{CONTACT_INFO.corpName}</span>
              <span className="hidden sm:block text-white/20">•</span>
              <span>All Rights Reserved</span>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-white/30 text-[10px] uppercase tracking-wider">We Accept</span>
              <div className="flex gap-1.5">
                {['VISA', 'MC', 'AMEX', 'DISC'].map((card) => (
                  <div key={card} className="px-2 py-1 bg-white text-ink-900 rounded-sm text-[9px] font-black tracking-wider">
                    {card}
                  </div>
                ))}
              </div>
            </div>

            <div className="text-[10px] text-white/30 uppercase tracking-wider">
              Charges appear as “<span className="text-athletic-400 font-bold">{CARD_DESCRIPTOR}</span>”
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
